import type {
  JobTemplateImportResult,
  SimulatedScannerCatalog,
  StageCaptureResult,
  StartSimulatedScanRequest,
} from '../shared/desktop-api';
import {
  readTour,
  readTourRemovals,
  writeTour,
  TOUR_CORPUS,
  TOUR_DOCUMENTS,
  TOUR_EVENT,
  TOUR_STEPS,
  type TourStepId,
} from '../renderer/tour';
import { rememberSampleJobSet, sampleSetOfJob } from '../renderer/sample-set-icons';
import { creationRefusal } from './job-creation';
import { refusalFor } from './not-carried';
import type { StageFiles } from './scan-session';
import { clearTourFeedFailed, feedWithRetries, noteTourFeedFailed } from './tour-feed';
import setupUrl from './assets/tour/accounts-payable-setup.json?url';
import invoiceUrl from './assets/tour/094_invoice_summit_APD2-094.tif?url';
import orderUrl from './assets/tour/017_purchase_order_meadow_APD2-017.tif?url';
import creditUrl from './assets/tour/020_credit_memo_cedar_valley_APD2-020.tif?url';
import w9Url from './assets/tour/021_w9_maple_harbor_APD2-021.tif?url';
import expenseUrl from './assets/tour/026_expense_report_northstar_APD2-026.tif?url';

/**
 * The tour's ready-made job, in the browser (CapturePoint 6a4ac6c9, 94b04eb1). The desktop installs
 * sample paperwork and a pre-trained job setup beside itself and feeds five of the pages through
 * its scanner simulator. Paige ships the same five fictional, clean pages and the same Accounts
 * Payable job setup with the web app (`assets/tour`, from CapturePoint's sample set "ap-demo-v2":
 * fictional people, companies and numbers throughout). The setup is imported as any job setup file
 * is (`POST /v1/jobs/setup-imports?sample=tour`: its six pages are read without credits), so the tour's job is a real Paige job, and the five pages go up
 * through the same upload as Add pages › files, so they are read by the same pools as anyone's.
 */
const TOUR_FILES: Readonly<Record<string, string>> = {
  '094_invoice_summit_APD2-094.tif': invoiceUrl,
  '017_purchase_order_meadow_APD2-017.tif': orderUrl,
  '020_credit_memo_cedar_valley_APD2-020.tif': creditUrl,
  '021_w9_maple_harbor_APD2-021.tif': w9Url,
  '026_expense_report_northstar_APD2-026.tif': expenseUrl,
};

/**
 * Upload is the feeder: the tour's five go in as one capture, at whatever pace the network has. Its pace is what the
 * scanner card shows and estimates from: five small pages go up in seconds (it said "1 pages / min" and "5 minutes").
 */
const FEEDER = {
  id: 'paige-upload',
  name: 'Paige upload',
  tier: 'cloud',
  pagesPerMinute: 60,
  description: 'Sample pages go up as files do.',
};

const CATALOG: SimulatedScannerCatalog = {
  models: [FEEDER],
  tray: {
    corpus: TOUR_CORPUS,
    label: 'Accounts Payable',
    pageCount: TOUR_DOCUMENTS.length,
    documentCount: TOUR_DOCUMENTS.length,
  },
  stacks: [
    {
      corpus: TOUR_CORPUS,
      label: 'Accounts Payable',
      documentCount: TOUR_DOCUMENTS.length,
      ready: true,
    },
  ],
};

export interface SampleTourOptions {
  /** The API's root (`/v1`). */
  apiOrigin: string;
  tenant?: string;
}

/** What the import answers: the Paige job, and the engine's account of what came with it. */
interface ImportedSetup {
  job: { id: string; workspaceJobId?: string | null };
  setup: JobTemplateImportResult;
}

/** The Paige job of each sample job this page imported, by its workspace job id. */
const imported = new Map<string, string>();

export function sampleTour(
  { apiOrigin, tenant }: SampleTourOptions,
  stage: StageFiles,
  bindChannel: (workspaceJobId: string, paigeJobId: string) => void,
): Record<string, unknown> {
  const headers = (extra: Record<string, string> = {}) => ({
    ...extra,
    ...(tenant ? { 'Paige-Tenant': tenant } : {}),
  });
  followSampleTour(apiOrigin, headers());
  return {
    getSimulatedScanners: async (): Promise<SimulatedScannerCatalog> => CATALOG,
    // Sent again by itself when the send gets no answer, and said in the tour's window if it still
    // does not get one (tour-feed.ts): never "0 of 5 documents" for good.
    startSimulatedScan: (request: StartSimulatedScanRequest) => feedSamples(stage, request),
    importSampleJobSetup: async (corpus: string): Promise<JobTemplateImportResult | null> => {
      if (corpus !== TOUR_CORPUS) return null;
      const answer = await importUnderFreeName(
        apiOrigin,
        headers,
        await (await fetch(setupUrl)).text(),
      );
      const workspaceJobId = answer.job.workspaceJobId ?? answer.setup.job.id;
      imported.set(workspaceJobId, answer.job.id);
      bindChannel(workspaceJobId, answer.job.id);
      return answer.setup;
    },
    // The readers are the pools'; there is nothing to download first.
    skipSampleReaderDownload: async () => undefined,
    onSampleReaderDownload: () => () => undefined,
    // The workbench's own Delete is refused (not-carried.ts) except for the tour's job, which the
    // tour removes when it ends, as CapturePoint's does: DELETE /v1/jobs/{id}, the ledger's delete.
    deleteJob: ({ jobId }: { jobId: string }) => deleteTourJob(apiOrigin, headers(), jobId),
  };
}

/** The tour's pages sent to its job, sent again when no answer came (tour-feed.ts); a failure is said in its window. */
async function feedSamples(
  stage: StageFiles,
  request: StartSimulatedScanRequest,
): Promise<StageCaptureResult> {
  const documents = request.documents ?? TOUR_DOCUMENTS;
  try {
    const files = await sampleFiles(documents);
    const capture = await feedWithRetries(() => stage(request.jobId, files, 'Sample documents'));
    clearTourFeedFailed(request.jobId);
    return { canceled: false, capture };
  } catch (error) {
    noteTourFeedFailed(request.jobId, request.corpus ?? TOUR_CORPUS, documents);
    throw error;
  }
}

/**
 * The setup imported as a new job. A name the company already uses is refused (409, the API's
 * JobSetupImport), and a tour started again while the last tour's job is still on its way out
 * would meet its own name: the next free one is taken instead, "Sample · Accounts Payable (2)",
 * as CapturePoint's importer numbers a taken name.
 */
async function importUnderFreeName(
  apiOrigin: string,
  headers: (extra?: Record<string, string>) => Record<string, string>,
  text: string,
): Promise<ImportedSetup> {
  const file = JSON.parse(text) as { job: { name: string } };
  const base = file.job.name;
  for (let attempt = 1; ; attempt++) {
    file.job.name = attempt === 1 ? base : `${base} (${attempt})`;
    const response = await fetch(`${apiOrigin}/jobs/setup-imports?sample=tour`, {
      method: 'POST',
      credentials: 'include',
      headers: headers({
        'content-type': 'application/json',
        'Idempotency-Key': crypto.randomUUID(),
      }),
      body: attempt === 1 ? text : JSON.stringify(file),
    });
    if (response.ok) return (await response.json()) as ImportedSetup;
    if (response.status !== 409 || attempt >= 5) throw new Error(await creationRefusal(response));
  }
}

/** DELETE /v1/jobs/{id} for the tour's job; any other job is refused as the workbench's Delete is. */
async function deleteTourJob(
  apiOrigin: string,
  headers: Record<string, string>,
  workspaceJobId: string,
): Promise<void> {
  if (!isTourJob(workspaceJobId)) throw new Error(refusalFor('deleteJob') ?? 'Not deleted.');
  const paigeJobId = await paigeJobFor(apiOrigin, headers, workspaceJobId);
  if (!paigeJobId) return;
  const response = await fetch(`${apiOrigin}/jobs/${paigeJobId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
  if (!response.ok && response.status !== 404) throw new Error(await creationRefusal(response));
}

/** The tour's own job: the one the tour runs on, one on its way out, or a sample job this browser made. */
function isTourJob(workspaceJobId: string): boolean {
  const tour = readTour();
  return (
    (tour?.jobId === workspaceJobId && !tour.own) ||
    readTourRemovals().includes(workspaceJobId) ||
    sampleSetOfJob(workspaceJobId) !== null
  );
}

/** The Paige job bound to a workspace job: the one this page imported, else the company's list. */
async function paigeJobFor(
  apiOrigin: string,
  headers: Record<string, string>,
  workspaceJobId: string,
): Promise<string | null> {
  const known = imported.get(workspaceJobId);
  if (known) return known;
  const response = await fetch(`${apiOrigin}/jobs`, { credentials: 'include', headers });
  if (!response.ok) throw new Error(await creationRefusal(response));
  const jobs = (await response.json()) as { id: string; workspaceJobId?: string | null }[];
  return jobs.find((job) => job.workspaceJobId === workspaceJobId)?.id ?? null;
}

/**
 * The named sample pages as files, each under its own name, in file-name order: the order the
 * desktop's simulated feeder stacks them, so the document starts are found, not given.
 */
async function sampleFiles(names: readonly string[]): Promise<File[]> {
  const chosen = names.filter((name) => name in TOUR_FILES).sort();
  return Promise.all(
    chosen.map(async (name) => {
      const response = await fetch(TOUR_FILES[name]);
      if (!response.ok) throw new Error("The tour's sample documents could not be loaded.");
      return new File([await response.blob()], name, { type: 'image/tiff' });
    }),
  );
}

/**
 * The tour picks up where it was (owner, 2026-10-06). The running tour lived only in the browser that
 * started it: opened anywhere else, its sample job was presented as the person's own work ("Paige read
 * your 5 documents. Want a quick tour?"). The job now carries the tour's step (`Job.sampleTour.step`,
 * kept by `followSampleTour`), and opening it writes the tour back into this browser before the
 * workbench mounts, exactly as a reload finds it. A browser that already holds this job's tour keeps
 * its own place. A tour that never reached its first card has no place to resume: `restart` asks the
 * caller for a fresh tour, which replaces this job.
 */
export function resumeSampleTour(
  workspaceJobId: string,
  step: string | null | undefined,
  mayTour = true,
): 'running' | 'resumed' | 'restart' | 'plain' {
  // Known as a sample job in this browser too: its picture, and never the offer of a tour "of your documents".
  rememberSampleJobSet(workspaceJobId, TOUR_CORPUS);
  const tour = readTour();
  if (tour?.jobId === workspaceJobId && !tour.own) return 'running';
  // Someone who cannot make a job cannot take the tour (it ends by removing this one): they just see it.
  if (!mayTour) return 'plain';
  if (!step || !(step in TOUR_STEPS)) return 'restart';
  writeTour({
    jobId: workspaceJobId,
    step: step as TourStepId,
    startedAt: new Date().toISOString(),
    ready: true,
  });
  return 'resumed';
}

/** Where the tour's place goes, and the last place sent: one listener for the page, whichever workbench is up. */
let following: { apiOrigin: string; headers: Record<string, string> } | null = null;
let sent = '';

/**
 * Keeps the sample job's record of the tour current (`PUT /v1/jobs/{id}/sample-tour`): the step each
 * time the tour moves, and "opened" when a workbench comes up on it, which is also what keeps the
 * nightly clean-up from removing a sample job somebody still uses. A place that could not be sent is
 * sent with the next one; the tour never waits on it.
 */
function followSampleTour(apiOrigin: string, headers: Record<string, string>): void {
  const first = following === null;
  following = { apiOrigin, headers };
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  if (first) window.addEventListener(TOUR_EVENT, () => void sendTourPlace());
  void sendTourPlace();
}

/** Sends the tour's place when it is new; exported for its test. */
export async function sendTourPlace(): Promise<void> {
  const tour = readTour();
  if (!following || !tour || tour.own) return;
  const step = tour.ready ? tour.step : null;
  const place = `${tour.jobId}:${step ?? ''}`;
  if (place === sent) return;
  const { apiOrigin, headers } = following;
  try {
    const paigeJobId = await paigeJobFor(apiOrigin, headers, tour.jobId);
    if (!paigeJobId) return;
    const response = await fetch(`${apiOrigin}/jobs/${paigeJobId}/sample-tour`, {
      method: 'PUT',
      credentials: 'include',
      // The step is sent as the person moves on, often onto another page: it outlives the one it left.
      keepalive: true,
      headers: { ...headers, 'content-type': 'application/json' },
      body: JSON.stringify({ step }),
    });
    if (response.ok) sent = place;
  } catch {
    /* Offline or refused: the next step carries the place. */
  }
}
